import { DragEvent, useRef, useState } from 'react';
import Button, { ButtonLink } from '~/components/new/Button';
import Alert from '~/components/ui/Alert';
import Icon, { RemixIconNames } from '~/components/ui/Icon';

const DEFAULT_MAX_SIZE = 5 * 1024 * 1024;

export type FileUploaderFile = {
    id?: number;
    name?: string;
    url?: string;
};

export type FileUploaderProps = {
    file?: any;
    initialFile?: any;
    disabled: boolean;
    onChange?: (file: FileUploaderFile | File | null) => void;
    onUpload: (file: File) => Promise<FileUploaderFile>;
    onRemove?: () => void;
    onWrongFileType?: () => void;
    onTooBig?: () => void;
    helpText?: string;
    hint?: string;
    errorText?: string;
    allowedFileTypes?: string[];
    maxSize?: number;
    canView?: boolean;
};

export default function FileUploader({
    file,
    initialFile,
    disabled,
    onChange,
    onUpload,
    onRemove,
    onWrongFileType = () => {},
    onTooBig = () => {},
    helpText = 'File up to 5MB',
    hint = '',
    errorText = 'There was an error uploading the file.',
    allowedFileTypes = [],
    maxSize = DEFAULT_MAX_SIZE,
    canView = true,
}: FileUploaderProps) {
    const [state, setState] = useState(file?.id ? 'uploaded' : 'initial');
    const [error, setError] = useState(false);
    const ref = useRef<HTMLInputElement>(null);

    const handleFiles = (files: File[] | FileList | null) => {
        setError(false);
        if (!files || files.length === 0 || disabled) return;

        const file = files[0];

        if (!isAllowedFileType(allowedFileTypes, file.type)) {
            onWrongFileType?.();
            return;
        }

        if (file.size > maxSize) {
            onTooBig?.();
            return;
        }

        setState('loading');
        onChange?.({ name: file.name });

        onUpload(file)
            .then((data) => {
                if (data) {
                    setState('uploaded');
                    onChange?.(data);
                } else {
                    setState('initial');
                    onChange?.(null);
                }
            })
            .catch(() => {
                setState('initial');
                onChange?.(null);
                setError(true);
            });
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (disabled) return;
        handleFiles(e.dataTransfer.files);
    };

    const handleRemove = () => {
        if (disabled) return;
        onChange?.(null);
        onRemove?.();
        setState('initial');
    };

    const showUploaded = !initialFile || initialFile.id !== file?.id;

    let content;
    switch (state) {
        case 'uploaded':
            content = (
                <div
                    className={
                        'file-uploader-file' + (showUploaded ? ' file-uploader-file-uploaded' : '')
                    }>
                    <div className='file-uploader-icon'>
                        <Icon
                            size={16}
                            variant='light'
                            icon={
                                showUploaded
                                    ? RemixIconNames.RiCheckLine
                                    : RemixIconNames.RiFile2Line
                            }
                        />
                    </div>
                    <div className='file-uploader-file-name'>
                        <div className='file-uploader-filename'>{file?.name}</div>
                        {showUploaded && (
                            <div className='file-uploader-status'>Importing completed</div>
                        )}
                    </div>
                    <div className='file-uploader-actions'>
                        {canView && (
                            <ButtonLink
                                disabled={!file?.url}
                                to={file?.url}
                                target='_blank'
                                variant='floating'
                                icon={RemixIconNames.RiEyeLine}
                                title='View file'
                                size='medium'
                            />
                        )}
                        <Button
                            variant='floating'
                            icon={RemixIconNames.RiDeleteBin6Line}
                            title='Delete file'
                            onClick={handleRemove}
                            size='medium'
                            data-test-name='deleteFile'
                        />
                    </div>
                </div>
            );
            break;
        case 'loading':
            content = (
                <div className='file-uploader-file'>
                    <div className='file-uploader-icon'>
                        <Icon
                            size={16}
                            variant='light'
                            icon='loading'
                            colorVar='action-contrast'
                        />
                    </div>
                    <div className='file-uploader-file-name'>
                        <div className='file-uploader-filename'>{file?.name}</div>
                        <div className='file-uploader-status'>Importing file...</div>
                    </div>
                </div>
            );
            break;
        default:
            content = (
                <div
                    className='file-uploader-placeholder rounded-4'
                    onClick={() => ref.current!.click()}
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}>
                    <div className='file-uploader-icon'>
                        <Icon
                            size={16}
                            variant='light'
                            icon={RemixIconNames.RiUploadCloud2Line}
                        />
                    </div>
                    <div className='file-uploader-placeholder-info'>
                        <div className='file-uploader-placeholder-action'>
                            Drag and drop or <span className='underline'>select file</span>
                        </div>
                        <div className='file-uploader-placeholder-help'>{helpText}</div>
                    </div>
                    <input
                        type='file'
                        ref={ref}
                        accept={allowedFileTypes.join(',')}
                        onChange={() => handleFiles(ref.current!.files)}
                    />
                </div>
            );
    }

    return (
        <div
            className='file-uploader'
            data-test-component='FileUploader'>
            {error && <Alert variant='danger'>{errorText}</Alert>}
            {content}
            {hint && <div className='form-help'>{hint}</div>}
        </div>
    );
}

export function File({ file }: { file: FileUploaderFile }) {
    return (
        <div
            className={'file-uploader-file'}
            data-test-component='FileUploader/File'>
            <Icon
                size={24}
                variant='light'
                icon={RemixIconNames.RiFile2Line}
            />
            <div className='file-uploader-file-name'>
                <div className='file-uploader-filename'>{file.name}</div>
            </div>
            <div className='file-uploader-actions'>
                <ButtonLink
                    to={file.url ?? ''}
                    target='_blank'
                    icon={RemixIconNames.RiEyeLine}
                    title='View file'
                    size='medium'
                />
            </div>
        </div>
    );
}

function isAllowedFileType(allowedList: string[], input: string): boolean {
    if (!allowedList || allowedList.length === 0) return true;

    for (const allowedType of allowedList) {
        if (allowedType.startsWith('.') && input.endsWith(allowedType)) {
            return true;
        }
        if (allowedType.endsWith('*') && input.startsWith(allowedType.slice(0, -1))) {
            return true;
        }
        if (allowedType === input) {
            return true;
        }
    }
    return false;
}
